import { useState } from "react";

// F6/ARCHITECTURE §4.3: both a direct <script> tag and a GTM custom HTML tag
// are supported. Direct embed is recommended for anti-flicker (loads earlier).
const TRACKER_SCRIPT_ORIGIN = import.meta.env.VITE_TRACKER_SCRIPT_ORIGIN || "https://tracker.example.com";

// The site key rides in the URL (?k=) rather than a data-key attribute: GTM's
// custom-HTML script injection preserves only the src, so the attribute form
// silently loses the key under tag managers (tracker/boot.ts has the details).
// The same one-liner therefore works for both direct paste and GTM.
function buildSnippet(siteKey: string): string {
  return `<script async src="${TRACKER_SCRIPT_ORIGIN}/t.js?k=${siteKey}"></script>`;
}

type Tab = "direct" | "gtm";

export function TagSnippet({ siteKey }: { siteKey: string }) {
  const [tab, setTab] = useState<Tab>("direct");
  const [copied, setCopied] = useState(false);

  const snippet = buildSnippet(siteKey);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard API unavailable; silently ignore
    }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <h2 className="text-base font-semibold mb-2">計測タグ</h2>
      <div className="flex gap-2 mb-3 border-b border-gray-200">
        <button
          type="button"
          onClick={() => setTab("direct")}
          className={`px-3 py-1.5 text-sm border-b-2 -mb-px ${
            tab === "direct"
              ? "border-blue-600 text-blue-600 font-medium"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          直貼りscript（推奨）
        </button>
        <button
          type="button"
          onClick={() => setTab("gtm")}
          className={`px-3 py-1.5 text-sm border-b-2 -mb-px ${
            tab === "gtm"
              ? "border-blue-600 text-blue-600 font-medium"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          GTMカスタムHTMLタグ
        </button>
      </div>

      {tab === "direct" ? (
        <p className="text-xs text-gray-500 mb-2">
          head内に直接設置してください。読込が早く、ABテストのアンチフリッカーに有利です。
        </p>
      ) : (
        <p className="text-xs text-gray-500 mb-2">
          GTMの「カスタムHTML」タグとして全ページ配信用トリガーで設置してください。GTM自体が非同期ロードのため、アンチフリッカーの適用が直貼りより遅れる場合があります。
        </p>
      )}

      <pre className="bg-gray-900 text-gray-100 text-xs rounded p-3 overflow-x-auto whitespace-pre-wrap break-all">
        <code>{snippet}</code>
      </pre>
      <button
        type="button"
        onClick={handleCopy}
        className="mt-2 rounded border border-gray-300 px-3 py-1 text-xs hover:bg-gray-100"
      >
        {copied ? "コピーしました" : "コピー"}
      </button>
    </div>
  );
}
